import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { IMAGES, CONTACT, ROOM_SPECS } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { CheckCircle2, Phone, Mail, FileText, Check, KeyRound } from "lucide-react";
import { useRoomAvailability, blockedDaysForGuests, feasibleForStay, findAlternatives, feasibleRoomCounts } from "@/lib/availability";
import { generateQuotePdf } from "@/lib/quotePdf";
import { TERMS } from "@/data/terms";
import { DIAL } from "@/data/countries";
import { Step1DatesGuests } from "@/components/booking/Step1DatesGuests";
import { Step2Rooms } from "@/components/booking/Step2Rooms";
import { Step3Details } from "@/components/booking/Step3Details";
import { Step4Summary } from "@/components/booking/Step4Summary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TODAY = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();

const fmtDate = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";

export default function BookingPage() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    arrival: params.get("arrival") || "", departure: params.get("departure") || "",
    guests: 2, guest_types: ["adult", "adult"], guest_ages: ["", ""], guest_cots: [false, false],
    first_name: "", last_name: "", email: "", phone_cc: "", phone_number: "", message: "",
  });
  const [step, setStep] = useState(1);
  const [roomsWanted, setRoomsWanted] = useState(1);
  const [search, setSearch] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selection, setSelection] = useState(null);
  const [state, setState] = useState({ submitting: false, success: false, error: "" });
  const [accepted, setAccepted] = useState(false);
  const terms = TERMS[lang] || TERMS.it;
  const v = (k) => t(`booking.v2.${k}`);

  const { blockedByRoom, loading: availLoading } = useRoomAvailability();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPhoneCc = (iso) => setForm((f) => ({ ...f, phone_cc: iso }));
  const setPhoneNumber = (val) => setForm((f) => ({ ...f, phone_number: val }));

  const setGuestsCount = (e) => {
    const n = Math.max(1, Math.min(7, Number(e.target.value) || 1));
    setForm((f) => {
      const types = [...(f.guest_types || [])], ages = [...(f.guest_ages || [])], cots = [...(f.guest_cots || [])];
      while (types.length < n) { types.push("adult"); ages.push(""); cots.push(false); }
      types.length = n; ages.length = n; cots.length = n;
      return { ...f, guests: n, guest_types: types, guest_ages: ages, guest_cots: cots };
    });
  };
  const setGuestType = (idx, val) => setForm((f) => {
    const types = [...(f.guest_types || [])], ages = [...(f.guest_ages || [])], cots = [...(f.guest_cots || [])];
    types[idx] = val;
    if (val === "adult") { ages[idx] = ""; cots[idx] = false; }
    return { ...f, guest_types: types, guest_ages: ages, guest_cots: cots };
  });
  const setGuestAge = (idx, val) => setForm((f) => {
    const ages = [...(f.guest_ages || [])], cots = [...(f.guest_cots || [])];
    ages[idx] = val;
    if (!(val !== "" && Number(val) < 3)) cots[idx] = false;
    return { ...f, guest_ages: ages, guest_cots: cots };
  });
  const setGuestCot = (idx, checked) => setForm((f) => {
    const cots = [...(f.guest_cots || [])]; cots[idx] = checked;
    return { ...f, guest_cots: cots };
  });

  const ADULT = { it: "Adulto", en: "Adult", fr: "Adulte" }[lang] || "Adulto";
  const CHILD = { it: "Bambino", en: "Child", fr: "Enfant" }[lang] || "Bambino";
  const GUEST_LABEL = { it: "Ospite", en: "Guest", fr: "Voyageur" }[lang] || "Ospite";
  const AGE_LABEL = { it: "Età", en: "Age", fr: "Âge" }[lang] || "Età";
  const YEARS = { it: "anni", en: "yrs", fr: "ans" }[lang] || "anni";
  const UNDER1 = { it: "< 1 anno", en: "< 1 yr", fr: "< 1 an" }[lang] || "< 1 anno";
  const COT_LABEL = { it: "Lettino per neonati (sotto i 3 anni)", en: "Baby cot (under 3 yrs)", fr: "Lit bébé (moins de 3 ans)" }[lang] || "Lettino per neonati (sotto i 3 anni)";
  const ERR_CHILD_AGE = { it: "Indica l'età di ogni bambino.", en: "Please indicate each child's age.", fr: "Veuillez indiquer l'âge de chaque enfant." }[lang] || "Indica l'età di ogni bambino.";
  const ERR_PHONE = { it: "Seleziona il prefisso internazionale e inserisci il numero di telefono.", en: "Please select the international dialing code and enter your phone number.", fr: "Veuillez sélectionner l'indicatif international et saisir votre numéro de téléphone." }[lang] || "Seleziona il prefisso internazionale e inserisci il numero di telefono.";
  const labels = { ADULT, CHILD, GUEST_LABEL, AGE_LABEL, YEARS, UNDER1, COT_LABEL };

  const effGuests = useMemo(() => {
    const types = form.guest_types || [], ages = form.guest_ages || [], cots = form.guest_cots || [];
    const n = types.reduce((acc, tp, i) => {
      const infantCot = tp === "child" && ages[i] !== "" && Number(ages[i]) < 3 && cots[i];
      return acc + (infantCot ? 0 : 1);
    }, 0);
    return Math.max(1, n);
  }, [form.guest_types, form.guest_ages, form.guest_cots]);

  const onRangeChange = (range) => setForm((f) => ({ ...f, arrival: fmtDate(range?.from), departure: fmtDate(range?.to) }));
  const calRange = {
    from: form.arrival ? new Date(form.arrival + "T00:00:00") : undefined,
    to: form.departure ? new Date(form.departure + "T00:00:00") : undefined,
  };
  const datesValid = form.arrival && form.departure && form.departure > form.arrival;

  const nightsCount = useMemo(() => {
    if (!datesValid) return 0;
    return Math.round((new Date(form.departure) - new Date(form.arrival)) / 86400000);
  }, [form.arrival, form.departure, datesValid]);

  // Max rooms selectable = min(3, guests); clamp when guest count changes.
  const maxRooms = Math.min(3, effGuests);
  useEffect(() => { if (roomsWanted > maxRooms) setRoomsWanted(maxRooms || 1); }, [maxRooms, roomsWanted]);

  // Live, client-side (no network): recolor calendar per guests + rooms + feasibility + alternatives.
  const calBlockedDays = useMemo(() => blockedDaysForGuests(blockedByRoom, effGuests, roomsWanted), [blockedByRoom, effGuests, roomsWanted]);
  const staysFeasible = useMemo(
    () => (datesValid && blockedByRoom ? feasibleForStay(blockedByRoom, form.arrival, form.departure, effGuests, roomsWanted) : null),
    [blockedByRoom, form.arrival, form.departure, effGuests, roomsWanted, datesValid]
  );
  const liveAlternatives = useMemo(
    () => (datesValid && staysFeasible === false && blockedByRoom ? findAlternatives(blockedByRoom, form.arrival, form.departure, effGuests, { roomsWanted }) : []),
    [blockedByRoom, form.arrival, form.departure, effGuests, roomsWanted, datesValid, staysFeasible]
  );
  const fallbackCounts = useMemo(
    () => (datesValid && staysFeasible === false && blockedByRoom ? feasibleRoomCounts(blockedByRoom, form.arrival, form.departure, effGuests).filter((r) => r !== roomsWanted) : []),
    [blockedByRoom, form.arrival, form.departure, effGuests, roomsWanted, datesValid, staysFeasible]
  );
  const applyDates = (a, d) => setForm((f) => ({ ...f, arrival: a, departure: d }));

  // Browser Back/Forward navigates between booking steps (keeps form progress).
  const goStep = (n) => { try { window.history.pushState({ bookingStep: n }, ""); } catch (e) {} setStep(n); };
  useEffect(() => {
    try { window.history.replaceState({ bookingStep: 1 }, ""); } catch (e) {}
    const onPop = (e) => setStep((e.state && e.state.bookingStep) || 1);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // On each step change, scroll back up to the step indicator (fixed navbar offset).
  useEffect(() => {
    const el = document.querySelector('[data-testid="booking-steps"]');
    const y = el ? el.getBoundingClientRect().top + window.scrollY - 90 : 0;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }, [step]);

  const downloadQuote = async (roomLabel, total, roomsCount, slugs) => {
    const rooms = (slugs || []).map((slug) => {
      const keys = (ROOM_SPECS[slug]?.amenities || []).filter((k) => !["towels", "desk", "shower"].includes(k));
      const services = [v("svcLinen"), v("svcTowels"), ...keys.map((k) => t(`rooms.amenities.${k}`))];
      return { label: roomName(slug), image: roomImg(slug), description: roomDesc(slug), services };
    });
    try {
      await generateQuotePdf({
        lang, roomLabel, roomsCount, rooms,
        arrival: form.arrival, departure: form.departure,
        nights: nightsCount, guests: effGuests, total,
        currency: (search && search.currency) || "EUR",
      });
    } catch (e) { /* no-op */ }
  };

  const roomName = (slug) => t(`rooms.${slug.split("_")[0]}.name`);
  const roomTag = (slug) => t(`rooms.${slug.split("_")[0]}.tag`);
  const roomDesc = (slug) => t(`rooms.${slug.split("_")[0]}.desc`);
  const roomImg = (slug) => (IMAGES.rooms[slug] || [])[0];

  const fmtHuman = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(lang, { day: "numeric", month: "short" });
  const fmtRange = (a, d) => `${fmtHuman(a)} – ${fmtHuman(d)} ${new Date(d + "T00:00:00").getFullYear()}`;

  const runSearch = async (a, d, g, rw) => {
    setSearchLoading(true); setSelection(null); setState((s) => ({ ...s, error: "" }));
    try {
      const res = await axios.get(`${API}/availability-search`, { params: { arrival: a, departure: d, guests: g, rooms_wanted: rw } });
      setSearch(res.data);
    } catch (e) {
      setSearch({ error: true });
    } finally {
      setSearchLoading(false);
    }
  };

  const goVerify = async () => {
    if (!datesValid) { setState({ submitting: false, success: false, error: t("booking.validation.dates") }); return; }
    const missingAge = (form.guest_types || []).some((gt, i) => gt === "child" && (form.guest_ages || [])[i] === "");
    if (missingAge) { setState({ submitting: false, success: false, error: ERR_CHILD_AGE }); return; }
    setState({ submitting: false, success: false, error: "" });
    goStep(2);
    await runSearch(form.arrival, form.departure, effGuests, roomsWanted);
  };

  const pickAlternative = async (a, d) => {
    setForm((f) => ({ ...f, arrival: a, departure: d }));
    goStep(2);
    await runSearch(a, d, effGuests, roomsWanted);
  };

  const chooseSingle = (o) => { setSelection({ rooms: [o.slug], total: o.total, capacity: o.max_guests }); setState({ submitting: false, success: false, error: "" }); goStep(3); };
  const chooseCombo = (c) => { setSelection({ rooms: c.rooms, total: c.total, capacity: c.capacity }); setState({ submitting: false, success: false, error: "" }); goStep(3); };

  const goSummary = () => {
    const req = ["first_name", "last_name", "email"];
    for (const k of req) if (!String(form[k] || "").trim()) { setState({ submitting: false, success: false, error: t("booking.validation.required") }); return; }
    if (!form.phone_cc || !String(form.phone_number || "").trim()) { setState({ submitting: false, success: false, error: ERR_PHONE }); return; }
    setState({ submitting: false, success: false, error: "" });
    goStep(4);
  };

  const confirm = async () => {
    if (!accepted) { setState({ submitting: false, success: false, error: t("booking.validation.acceptTerms") }); return; }
    setState({ submitting: true, success: false, error: "" });
    const guests_detail = (form.guest_types || []).map((type, i) => {
      const age = (form.guest_ages || [])[i];
      const hasAge = type === "child" && age !== "";
      return { type, age: hasAge ? Number(age) : null, cot: hasAge && Number(age) < 3 ? !!(form.guest_cots || [])[i] : false };
    });
    try {
      await axios.post(`${API}/bookings`, {
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        phone: `${DIAL[form.phone_cc] || ""} ${form.phone_number}`.trim(),
        message: form.message,
        guests: Number(form.guests), guest_types: form.guest_types, guests_detail,
        arrival: form.arrival, departure: form.departure,
        rooms: selection.rooms, room: selection.rooms[0] || null,
        language: lang,
      });
      setState({ submitting: false, success: true, error: "" });
    } catch (err) {
      if (err?.response?.status === 409) {
        const detail = err?.response?.data?.detail;
        setState({ submitting: false, success: false, error: (detail && detail.message) || t("booking.quote.unavailable") });
        goStep(2);
        runSearch(form.arrival, form.departure, effGuests, roomsWanted);
      } else {
        setState({ submitting: false, success: false, error: t("booking.error") });
      }
    }
  };

  const STEPS = v("steps") || [];

  const ctx = {
    t, v, lang, TODAY, form, update,
    setPhoneCc, setPhoneNumber,
    calRange, onRangeChange, calBlockedDays, availLoading,
    setGuestsCount, labels, setGuestType, setGuestAge, setGuestCot,
    effGuests, maxRooms, roomsWanted, setRoomsWanted,
    datesValid, staysFeasible, fallbackCounts, liveAlternatives, applyDates, fmtRange,
    state, goVerify, searchLoading, search,
    roomName, roomTag, roomDesc, roomImg, downloadQuote, chooseSingle, chooseCombo, pickAlternative,
    STEPS, goSummary, nightsCount, selection, accepted, setAccepted, confirm,
  };

  if (state.success) {
    return (
      <section className="pt-40 pb-24 min-h-[70vh] bg-[#F2EFEB]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <Reveal>
            <div data-testid="booking-success" className="bg-white p-10 rounded-3xl border border-black/5">
              <CheckCircle2 className="w-14 h-14 text-[#2A4B3C] mx-auto mb-5" />
              <h1 className="font-display text-3xl md:text-4xl text-[#1A1A1A]">{t("booking.success")}</h1>
              <p className="mt-4 text-[#5C5C5C]">{t("booking.subtitle")}</p>
              <a href={`mailto:${CONTACT.email}`} className="mt-6 inline-flex items-center gap-2 text-[#1D3557] underline underline-offset-2">
                <Mail className="w-4 h-4" /> {CONTACT.email}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="pt-36 pb-10 bg-[#F2EFEB]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <h1 className="font-display text-5xl md:text-6xl text-[#1A1A1A] tracking-tight">{t("booking.title")}</h1>
            <p className="mt-5 text-lg text-[#5C5C5C] max-w-2xl">{t("booking.subtitle")}</p>
            <div className="mt-6 inline-flex flex-wrap items-center gap-3 px-5 py-3 rounded-2xl bg-white border border-[#1D3557]/15">
              <span className="text-xs uppercase tracking-widest text-[#1D3557] font-semibold">Direct booking</span>
              <span className="text-sm text-[#5C5C5C]">{lang === "it" ? "Miglior prezzo garantito · Nessuna commissione · Contatto diretto con i proprietari" : lang === "fr" ? "Meilleur prix garanti · Aucune commission · Contact direct avec les propriétaires" : "Best price guarantee · No fees · Direct contact with owners"}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Step indicator */}
          <Reveal>
            <div data-testid="booking-steps" className="flex items-center gap-2 md:gap-3 mb-8">
              {STEPS.map((label, i) => {
                const n = i + 1;
                const activeState = step === n ? "active" : step > n ? "done" : "todo";
                return (
                  <React.Fragment key={n}>
                    <div className="flex items-center gap-2">
                      <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold flex-shrink-0 transition-colors ${activeState === "todo" ? "bg-black/5 text-[#5C5C5C]" : "bg-[#1D3557] text-white"}`}>
                        {activeState === "done" ? <Check className="w-4 h-4" /> : n}
                      </span>
                      <span className={`hidden sm:block text-sm ${step === n ? "text-[#1A1A1A] font-medium" : "text-[#5C5C5C]"}`}>{label}</span>
                    </div>
                    {n < STEPS.length && <span className="flex-1 h-px bg-black/10" />}
                  </React.Fragment>
                );
              })}
            </div>
          </Reveal>

          <Reveal>
            <div className="bg-white p-6 md:p-10 rounded-2xl border border-black/5 space-y-6">
              {step === 1 && <Step1DatesGuests ctx={ctx} />}
              {step === 2 && <Step2Rooms ctx={ctx} />}
              {step === 3 && <Step3Details ctx={ctx} />}
              {step === 4 && <Step4Summary ctx={ctx} />}
            </div>
          </Reveal>

          {/* Self check-in note */}
          <Reveal>
            <div data-testid="booking-self-checkin" className="mt-6 flex items-start gap-3 px-5 py-4 rounded-2xl bg-white border border-[#1D3557]/15">
              <KeyRound className="w-5 h-5 text-[#1D3557] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#1D3557]">{t("selfCheckin.title")}</p>
                <p className="mt-1 text-sm text-[#5C5C5C] leading-relaxed">{t("selfCheckin.text")}</p>
              </div>
            </div>
          </Reveal>

          {/* Terms, conditions & house rules */}
          <Reveal>
            <div id="terms" data-testid="terms-section" className="mt-8 bg-white p-8 md:p-10 rounded-2xl border border-black/5 scroll-mt-28">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-[#1D3557]" />
                <h2 className="font-display text-2xl md:text-3xl text-[#1A1A1A]">{terms.title}</h2>
              </div>
              <div className="space-y-6">
                {terms.sections.map((s, i) => (
                  <div key={i}>
                    <h3 className="font-display text-lg text-[#1D3557] mb-2">{s.h}</h3>
                    <ul className="space-y-1.5">
                      {s.p.map((para, k) => (
                        <li key={k} className="flex items-start gap-2 text-sm text-[#5C5C5C] leading-relaxed">
                          <Check className="w-4 h-4 text-[#345B73] flex-shrink-0 mt-0.5" /> {para}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Contact + photo */}
          <Reveal delay={100}>
            <div className="mt-8 grid md:grid-cols-2 gap-6 items-stretch">
              <div className="bg-[#F2EFEB] p-6 rounded-2xl flex flex-col justify-center">
                <h3 className="font-display text-xl text-[#1A1A1A] mb-4">{t("common.contact")}</h3>
                <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#345B73] mb-3">
                  <Phone className="w-4 h-4 text-[#345B73]" /> {CONTACT.phoneDisplay}
                </a>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#345B73] break-all">
                  <Mail className="w-4 h-4 text-[#345B73]" /> {CONTACT.email}
                </a>
              </div>
              <div className="h-56 md:h-auto min-h-[14rem] rounded-2xl overflow-hidden">
                <img src="/assets/fr51swu0_20200507_221301.jpg" alt="Lago di Como, Bellagio - tempietto di Villa Melzi" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

